import React, { useState, useEffect, useRef } from "react";
import { Play, Square, Beaker, RotateCcw, Eye, Settings } from "lucide-react";

const WaterPouringSimulator = () => {
  const [cupA, setCupA] = useState({ capacity: 4, current: 0, visible: false });
  const [cupB, setCupB] = useState({ capacity: 9, current: 0, visible: false });
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationType, setAnimationType] = useState("");
  const [records, setRecords] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  const animationRef = useRef(null);

  // 添加操作记录
  const addRecord = (action, cupAAmount, cupBAmount) => {
    const newRecord = {
      id: Date.now(),
      action,
      cupA: cupAAmount,
      cupB: cupBAmount,
    };
    setRecords((prev) => [...prev, newRecord]);
  };

  // 停止所有动画
  const stopAnimation = () => {
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;

      // 记录被中断的操作
      if (animationType) {
        let actionDescription = "";
        if (animationType.includes("fill-A")) {
          actionDescription = "水杯A装水(中断)";
        } else if (animationType.includes("fill-B")) {
          actionDescription = "水杯B装水(中断)";
        } else if (animationType.includes("empty-A")) {
          actionDescription = "水杯A倒空(中断)";
        } else if (animationType.includes("empty-B")) {
          actionDescription = "水杯B倒空(中断)";
        } else if (animationType.includes("pour-A-to-B")) {
          actionDescription = "从A倒向B(中断)";
        } else if (animationType.includes("pour-B-to-A")) {
          actionDescription = "从B倒向A(中断)";
        }

        if (actionDescription) {
          addRecord(actionDescription, cupA.current, cupB.current);
        }
      }
    }
    setIsAnimating(false);
    setAnimationType("");
    setCupA((prev) => ({ ...prev, visible: false }));
    setCupB((prev) => ({ ...prev, visible: false }));
  };

  // 测量水量
  const measureWater = () => {
    setCupA((prev) => ({ ...prev, visible: true }));
    setCupB((prev) => ({ ...prev, visible: true }));
    // 测量操作不记录
  };

  // 装满水杯
  const fillCup = (cup) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setAnimationType(`fill-${cup}`);

    const targetCup = cup === "A" ? cupA : cupB;
    const setCup = cup === "A" ? setCupA : setCupB;

    let current = targetCup.current;
    const target = targetCup.capacity;
    const step = (target - current) / 20;

    animationRef.current = setInterval(() => {
      current += step;
      if (current >= target) {
        current = target;
        setCup((prev) => ({ ...prev, current: target, visible: false }));
        setIsAnimating(false);
        setAnimationType("");
        clearInterval(animationRef.current);
        addRecord(
          `水杯${cup}装满`,
          cup === "A" ? target : cupA.current,
          cup === "B" ? target : cupB.current
        );
      } else {
        setCup((prev) => ({ ...prev, current, visible: false }));
      }
    }, 50);
  };

  // 倒空水杯
  const emptyCup = (cup) => {
    if (isAnimating) return;

    setIsAnimating(true);
    setAnimationType(`empty-${cup}`);

    const targetCup = cup === "A" ? cupA : cupB;
    const setCup = cup === "A" ? setCupA : setCupB;

    let current = targetCup.current;
    const step = current / 20;

    animationRef.current = setInterval(() => {
      current -= step;
      if (current <= 0) {
        current = 0;
        setCup((prev) => ({ ...prev, current: 0, visible: false }));
        setIsAnimating(false);
        setAnimationType("");
        clearInterval(animationRef.current);
        addRecord(
          `水杯${cup}倒空`,
          cup === "A" ? 0 : cupA.current,
          cup === "B" ? 0 : cupB.current
        );
      } else {
        setCup((prev) => ({ ...prev, current, visible: false }));
      }
    }, 50);
  };

  // 从一个杯子倒到另一个杯子
  const pourWater = (from, to) => {
    if (isAnimating) return;

    const fromCup = from === "A" ? cupA : cupB;
    const toCup = to === "A" ? cupA : cupB;

    if (fromCup.current <= 0) return;

    setIsAnimating(true);
    setAnimationType(`pour-${from}-to-${to}`);

    // 计算实际能倒的水量：不能超过源杯子的水量，也不能超过目标杯子的剩余容量
    const maxPour = Math.min(fromCup.current, toCup.capacity - toCup.current);
    const steps = 30;
    const stepAmount = maxPour / steps;

    let poured = 0;
    const initialFromAmount = fromCup.current;
    const initialToAmount = toCup.current;

    animationRef.current = setInterval(() => {
      poured += stepAmount;

      if (poured >= maxPour) {
        // 最终状态
        const finalFromAmount = initialFromAmount - maxPour;
        const finalToAmount = initialToAmount + maxPour;

        if (from === "A") {
          setCupA((prev) => ({
            ...prev,
            current: finalFromAmount,
            visible: false,
          }));
          setCupB((prev) => ({
            ...prev,
            current: finalToAmount,
            visible: false,
          }));
        } else {
          setCupB((prev) => ({
            ...prev,
            current: finalFromAmount,
            visible: false,
          }));
          setCupA((prev) => ({
            ...prev,
            current: finalToAmount,
            visible: false,
          }));
        }

        setIsAnimating(false);
        setAnimationType("");
        clearInterval(animationRef.current);

        addRecord(
          `从${from}倒向${to}`,
          from === "A" ? finalFromAmount : finalToAmount,
          to === "A" ? finalFromAmount : finalToAmount
        );
      } else {
        // 中间状态
        const currentFromAmount = initialFromAmount - poured;
        const currentToAmount = initialToAmount + poured;

        if (from === "A") {
          setCupA((prev) => ({
            ...prev,
            current: currentFromAmount,
            visible: false,
          }));
          setCupB((prev) => ({
            ...prev,
            current: currentToAmount,
            visible: false,
          }));
        } else {
          setCupB((prev) => ({
            ...prev,
            current: currentFromAmount,
            visible: false,
          }));
          setCupA((prev) => ({
            ...prev,
            current: currentToAmount,
            visible: false,
          }));
        }
      }
    }, 50);
  };

  // 重置（只清空水量，保持容量设置）
  const reset = () => {
    stopAnimation();
    setCupA((prev) => ({ ...prev, current: 0, visible: false }));
    setCupB((prev) => ({ ...prev, current: 0, visible: false }));
    setRecords([]);
  };

  // 格式化水量显示
  const formatVolume = (volume) => {
    return volume % 1 === 0 ? `${volume}L` : `${volume.toFixed(1)}L`;
  };

  // 水杯组件
  const Cup = ({ cup, label, capacity, current, visible }) => {
    // 根据容量调整水杯大小，最小宽度32px，最大宽度80px
    const cupWidth = Math.max(32, Math.min(80, 32 + capacity * 4));
    // 根据容量调整高度，最小高度120px，最大高度300px
    const cupHeight = Math.max(120, Math.min(300, 120 + capacity * 15));

    const waterHeight = (current / capacity) * cupHeight;
    const waterColor = "#3b82f6"; // 统一使用蓝色

    return (
      <div className="flex flex-col items-center space-y-2">
        <div className="text-lg font-semibold text-gray-700">{label}</div>
        <div className="relative">
          {/* 杯子外框 - 移除刻度线 */}
          <div
            className="border-4 border-gray-400 bg-gray-50 relative overflow-hidden"
            style={{
              width: `${cupWidth}px`,
              height: `${cupHeight}px`,
              borderRadius: "0 0 8px 8px",
            }}
          >
            {/* 水 */}
            <div
              className="absolute bottom-0 w-full transition-all duration-300 ease-out"
              style={{
                height: `${waterHeight}px`,
                backgroundColor: waterColor,
                opacity: 0.7,
              }}
            />
          </div>

          {/* 容量标签 */}
          <div className="text-sm text-gray-500 text-center mt-1">
            {capacity}L
          </div>

          {/* 当前水量显示 - 只有测量后才显示 */}
          <div className="text-sm font-medium text-center mt-1">
            {visible ? formatVolume(current) : "?"}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          倒水问题模拟器
        </h1>
        <p className="text-gray-600">模拟经典的倒水谜题，观察水量变化过程</p>
      </div>

      {/* 设置面板 */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Settings size={20} />
            设置
          </h3>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {showSettings ? "隐藏" : "显示"}
          </button>
        </div>

        {showSettings && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                水杯A容量 (L)
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={cupA.capacity}
                onChange={(e) => {
                  const newCapacity = parseInt(e.target.value) || 4;
                  setCupA((prev) => ({
                    ...prev,
                    capacity: newCapacity,
                    current: Math.min(prev.current, newCapacity), // 确保当前水量不超过新容量
                  }));
                }}
                className="w-full p-2 border rounded"
                disabled={isAnimating}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                水杯B容量 (L)
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={cupB.capacity}
                onChange={(e) => {
                  const newCapacity = parseInt(e.target.value) || 9;
                  setCupB((prev) => ({
                    ...prev,
                    capacity: newCapacity,
                    current: Math.min(prev.current, newCapacity), // 确保当前水量不超过新容量
                  }));
                }}
                className="w-full p-2 border rounded"
                disabled={isAnimating}
              />
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 左侧：水杯显示 */}
        <div className="lg:col-span-2">
          <div className="flex justify-center items-end space-x-12 mb-8">
            <Cup
              cup="A"
              label="水杯 A"
              capacity={cupA.capacity}
              current={cupA.current}
              visible={cupA.visible}
            />
            <Cup
              cup="B"
              label="水杯 B"
              capacity={cupB.capacity}
              current={cupB.current}
              visible={cupB.visible}
            />
          </div>

          {/* 操作按钮 */}
          <div className="space-y-4">
            {/* 装满/倒空按钮 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-center">水杯 A</h4>
                <button
                  onClick={() => fillCup("A")}
                  disabled={isAnimating}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  <Beaker size={16} />
                  装满
                </button>
                <button
                  onClick={() => emptyCup("A")}
                  disabled={isAnimating}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  <RotateCcw size={16} />
                  倒空
                </button>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium text-center">水杯 B</h4>
                <button
                  onClick={() => fillCup("B")}
                  disabled={isAnimating}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                >
                  <Beaker size={16} />
                  装满
                </button>
                <button
                  onClick={() => emptyCup("B")}
                  disabled={isAnimating}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50"
                >
                  <RotateCcw size={16} />
                  倒空
                </button>
              </div>
            </div>

            {/* 倒水按钮 */}
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => pourWater("A", "B")}
                disabled={isAnimating}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              >
                A → B 倒水
              </button>
              <button
                onClick={() => pourWater("B", "A")}
                disabled={isAnimating}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50"
              >
                B → A 倒水
              </button>
            </div>

            {/* 控制按钮 */}
            <div className="grid grid-cols-3 gap-4">
              <button
                onClick={stopAnimation}
                disabled={!isAnimating}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50"
              >
                <Square size={16} />
                停止
              </button>
              <button
                onClick={measureWater}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
              >
                <Eye size={16} />
                测量
              </button>
              <button
                onClick={reset}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                <RotateCcw size={16} />
                重置
              </button>
            </div>
          </div>

          {/* 动画状态显示 */}
          {isAnimating && (
            <div className="mt-4 p-3 bg-yellow-100 border border-yellow-300 rounded">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                <span className="text-yellow-800">
                  正在执行: {animationType}...
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 右侧：操作记录 */}
        <div>
          <h3 className="text-lg font-semibold mb-4">操作记录</h3>
          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
            {records.length === 0 ? (
              <p className="text-gray-500 text-center">暂无操作记录</p>
            ) : (
              <div className="bg-white rounded border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">
                        操作
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">
                        A杯({cupA.capacity}L)
                      </th>
                      <th className="px-3 py-2 text-center font-medium text-gray-700">
                        B杯({cupB.capacity}L)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record, index) => (
                      <tr
                        key={record.id}
                        className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {record.action}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-600">
                          {formatVolume(record.cupA)}
                        </td>
                        <td className="px-3 py-2 text-center text-gray-600">
                          {formatVolume(record.cupB)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {records.length > 0 && (
            <button
              onClick={() => setRecords([])}
              className="w-full mt-3 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              清空记录
            </button>
          )}
        </div>
      </div>
      <footer className="mt-8 w-full text-center text-sm text-gray-500">
        © {new Date().getFullYear()} 孙维刚教育研究院-陈硕老师 保留所有权利。
      </footer>
    </div>
  );
};

export default WaterPouringSimulator;
